import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  Menu,
  MenuItem,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UpgradeDialogSource } from "schemas/dashboard";
import { routes } from "schemas/routes";

import {
  OrganizationTemplateRubric,
  OrganizationTemplateRubricSuggestion,
  Template,
  TemplateContentType,
  TemplateRubricName,
} from "features/Influencer/ContactList/schema";
import {
  calculateScore,
  getLatestRubricByName,
  getScoreColor,
} from "features/Influencer/Templates/TemplatesTable/helpers";
import { fetcherAuth } from "utils/api";
import { isPage } from "utils/general";

import HalfDoughnut from "./HalfDoughnut";
import Rubrics from "./Rubrics";

// When a first-time user creates an outreach template, the backend auto-runs an
// evaluation in the background. Poll for the resulting score so it appears
// without a manual refresh.
const AUTO_SCORE_POLL_INTERVAL_MS = 5000;
const AUTO_SCORE_MAX_ATTEMPTS = 18; // ~90s, matches the eval's worst-case latency
const READY_SCORE_THRESHOLD = 70;

const StatusChip = ({
  iconClass,
  label,
  bg,
  color,
}: {
  iconClass: string;
  label: string;
  bg: string;
  color: string;
}) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.75,
      px: 1.25,
      py: 0.5,
      borderRadius: 999,
      backgroundColor: bg,
    }}
  >
    <Box component="span" className={iconClass} sx={{ fontSize: 12, color }} />
    <Typography variant="caption" sx={{ fontWeight: 600, color }}>
      {label}
    </Typography>
  </Box>
);

interface Props {
  template: Template;
  setTemplate: React.Dispatch<React.SetStateAction<Template>>;
  // When provided, the "Restore template to this point" button in the
  // revision-preview dialog calls this. The parent is responsible for both
  // updating the template state AND bumping a `restoreSignal` on
  // `TemplateBody` so the TipTap editors actually pick up the new content.
  onRestoreContent?: (subject: string, body: string) => void;
  // Bumped by the parent to trigger an immediate re-evaluation (e.g. when the
  // user clicks the suggestions chip in the editor).
  runEvaluateSignal?: number;
  onLoadingChange?: (loading: boolean) => void;
  onEvaluated?: (result: {
    suggestions: OrganizationTemplateRubricSuggestion[];
    highlightedBody: string | null;
  }) => void;
}

const TemplateRubrics = ({
  template,
  setTemplate,
  onRestoreContent,
  runEvaluateSignal,
  onLoadingChange,
  onEvaluated,
}: Props) => {
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { setErrorAlert } = useContext(AlertContext);
  const { currentOrg } = useContext(OrganizationUserContext);
  const { setTemplates, setCreateTemplateDialogOpen } = useContext(
    OutreachTemplatesContext,
  );
  const {
    setUpgradeDialogSource,
    remainingTemplateEvaluations,
    totalTemplateEvaluations,
    setRemainingTemplateEvaluations,
  } = useContext(SubscriptionContext);
  const [rubric, setRubric] = useState<OrganizationTemplateRubric | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [autoScoring, setAutoScoring] = useState<boolean>(false);
  const isBusy = loading || autoScoring;
  const [breakdownOpen, setBreakdownOpen] = useState<boolean>(false);
  const [historyAnchorEl, setHistoryAnchorEl] = useState<HTMLElement | null>(
    null,
  );
  const [revisionPreviewOpen, setRevisionPreviewOpen] =
    useState<boolean>(false);

  const rubricName =
    template.contentType === TemplateContentType.FOLLOWUP
      ? TemplateRubricName.followup_default
      : TemplateRubricName.outreach_default;
  const sortedRubrics = (template?.rubrics ?? [])
    .filter((r) => r.name === rubricName)
    .slice()
    .sort((a, b) => b.createdAt - a.createdAt);
  const latestRubric = sortedRubrics[0] ?? null;
  const isViewingPastRubric =
    !!rubric &&
    !!latestRubric &&
    rubric.id !== undefined &&
    latestRubric.id !== undefined &&
    rubric.id !== latestRubric.id;
  const canRestoreFromRubric =
    isViewingPastRubric && !!rubric?.content && rubric.content.length > 0;

  const score = rubric ? calculateScore(rubric) : 0;
  const isReadyToGo = !isBusy && !!rubric && score >= READY_SCORE_THRESHOLD;

  const handleOpenHistory = (e: React.MouseEvent<HTMLElement>) => {
    if (sortedRubrics.length === 0) {
      if (!currentOrg?.isPaidUser) {
        setUpgradeDialogSource(
          UpgradeDialogSource.template_limit_increase_chip,
        );
      }
      return;
    }
    setHistoryAnchorEl(e.currentTarget);
  };

  const handleSelectFromHistory = (selected: OrganizationTemplateRubric) => {
    setRubric(selected);
    setHistoryAnchorEl(null);
  };

  const handleOpenRevisionPreview = () => {
    if (!rubric?.content) return;
    setRevisionPreviewOpen(true);
  };

  const handleConfirmRestore = () => {
    if (!rubric?.content) return;
    const { subject, body } = parseRubricContent(rubric.content);
    if (onRestoreContent) {
      onRestoreContent(subject, body);
    } else {
      // Fallback for consumers that haven't wired up `onRestoreContent` yet —
      // updates state but won't visually refresh the TipTap editors.
      setTemplate((prev) => ({
        ...prev,
        body,
        ...(subject ? { subject } : {}),
      }));
    }
    setRevisionPreviewOpen(false);
  };

  const handleEvaluate = async () => {
    if (
      remainingTemplateEvaluations !== null &&
      remainingTemplateEvaluations <= 0
    ) {
      setUpgradeDialogSource(UpgradeDialogSource.template_evaluation_limit);
      return;
    }
    try {
      setLoading(true);
      setRemainingTemplateEvaluations((prev) =>
        prev !== null ? prev - 1 : prev,
      );
      const createNewTemplate = !template?.id;
      const {
        rubric,
        template: updatedTemplate,
        suggestions,
        highlightedBody,
      } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/templates/evaluate`,
        "POST",
        {},
        {
          // We still need to pass subject and body in as user might change
          // the template and re-evaluate without saving the template
          templateId: template?.id,
          body: template?.body,
          subject: template?.subject,
          contentType: template?.contentType,
          name: template?.name,
          attachment: template?.attachment,
        },
      );
      setRubric(rubric);
      if (updatedTemplate) {
        setTemplate(updatedTemplate);
        setTemplates((prev) =>
          prev.map((t) => (t.id === updatedTemplate.id ? updatedTemplate : t)),
        );
      }
      onEvaluated?.({
        suggestions: suggestions ?? [],
        highlightedBody: highlightedBody ?? null,
      });
      // If the current URL does not have a template id, navigate to that
      if (isPage(routes.templates) && createNewTemplate) {
        setCreateTemplateDialogOpen(false);
        navigate(`/${routes.templates}/${updatedTemplate.id}`, {
          replace: true,
        });
      }
    } catch (error) {
      if (
        error instanceof Error &&
        error.message.includes("hit the limit of using the template evaluator")
      ) {
        setUpgradeDialogSource(UpgradeDialogSource.template_evaluation_limit);
      } else {
        setErrorAlert(error);
      }
      setRemainingTemplateEvaluations((prev) =>
        prev !== null ? prev + 1 : prev,
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const rubricName =
      template.contentType === TemplateContentType.FOLLOWUP
        ? TemplateRubricName.followup_default
        : TemplateRubricName.outreach_default;
    const latestRubric = getLatestRubricByName(rubricName, template);
    setRubric(latestRubric);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template?.rubrics?.length, template?.contentType]);

  useEffect(() => {
    if (!runEvaluateSignal) return;
    handleEvaluate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runEvaluateSignal]);

  useEffect(() => {
    onLoadingChange?.(isBusy);
  }, [isBusy, onLoadingChange]);

  // Poll for the background auto-run score on a freshly created outreach
  // template (first-time users only). Stops once a score lands or attempts run out.
  useEffect(() => {
    const rubricName = TemplateRubricName.outreach_default;
    const shouldPoll =
      Boolean(template?.autoRunEnqueued) &&
      Boolean(template?.id) &&
      Boolean(currentOrg?.id) &&
      template.contentType === TemplateContentType.OUTREACH &&
      !getLatestRubricByName(rubricName, template);

    if (!shouldPoll) {
      setAutoScoring(false);
      return;
    }

    let cancelled = false;
    let attempts = 0;
    let timeoutId: ReturnType<typeof setTimeout>;
    setAutoScoring(true);

    const poll = async () => {
      attempts += 1;
      try {
        const { template: fetched } = await fetcherAuth(
          getToken,
          `/api/organization/${currentOrg?.id}/templates/${template.id}`,
        );
        const latest = getLatestRubricByName(rubricName, fetched);
        if (latest && !cancelled) {
          setRubric(latest);
          // Merge in the fetched rubrics only, preserving any in-progress edits
          // the user has made to the template body/subject.
          setTemplate((prev) => ({ ...prev, rubrics: fetched.rubrics }));
          setAutoScoring(false);
          return;
        }
      } catch {
        // Ignore transient errors and keep polling until attempts run out.
      }
      if (cancelled) return;
      if (attempts >= AUTO_SCORE_MAX_ATTEMPTS) {
        setAutoScoring(false);
        return;
      }
      timeoutId = setTimeout(poll, AUTO_SCORE_POLL_INTERVAL_MS);
    };

    timeoutId = setTimeout(poll, AUTO_SCORE_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    template?.id,
    template?.autoRunEnqueued,
    template?.contentType,
    template?.rubrics?.length,
  ]);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
      }}
    >
      {isReadyToGo ? (
        <Box sx={{ textAlign: "center", mb: 1 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 1,
              mb: 1.5,
            }}
          >
            <StatusChip
              iconClass="fa-solid fa-trophy"
              label="Strong pitch"
              bg="#F2FCF2"
              color="#2E7D32"
            />
            <StatusChip
              iconClass="fa-solid fa-circle-check"
              label="Ready to send"
              bg="#DBEFDD"
              color="#165C4E"
            />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            Your pitch is ready to go
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ maxWidth: 460, mx: "auto" }}
          >
            You&apos;ve nailed the essentials — a clear hook, personal touch,
            and a reason for brands to reply.
          </Typography>
        </Box>
      ) : (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              mb: 1,
            }}
          >
            <Box
              onClick={handleOpenHistory}
              sx={{
                fontSize: 10,
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                px: 1.5,
                py: 0.5,
                borderRadius: 999,
                border: "1px solid",
                borderColor: "purple.light",
                bgcolor: "#fff",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "text.secondary",
                cursor:
                  sortedRubrics.length > 0 || !currentOrg?.isPaidUser
                    ? "pointer"
                    : "default",
              }}
            >
              {`${(totalTemplateEvaluations || 0) - (remainingTemplateEvaluations || 0)}/${totalTemplateEvaluations ?? "—"} evaluations used`}
              {sortedRubrics.length > 0 && (
                <Box
                  component="span"
                  className="fa-solid fa-chevron-down"
                  sx={{ fontSize: 10 }}
                />
              )}
            </Box>
          </Box>

          <Menu
            anchorEl={historyAnchorEl}
            open={!!historyAnchorEl}
            onClose={() => setHistoryAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
          >
            {sortedRubrics.map((r, idx) => {
              const isSelected = !!rubric?.id && r.id === rubric.id;
              const itemScore = calculateScore(r);
              const itemColors = getScoreColor(itemScore);
              return (
                <MenuItem
                  key={r.id ?? r.createdAt}
                  selected={isSelected}
                  onClick={() => handleSelectFromHistory(r)}
                  sx={{
                    py: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {`Evaluation #${sortedRubrics.length - idx}`}
                      {idx === 0 ? " (latest)" : ""}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(r.createdAt * 1000).toLocaleDateString(
                        "en-US",
                        {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        },
                      )}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      flexShrink: 0,
                      px: 1,
                      py: 0.25,
                      borderRadius: 999,
                      backgroundColor: itemColors.track,
                      color: itemColors.text,
                      fontSize: 12,
                      fontWeight: 700,
                      minWidth: 44,
                      textAlign: "center",
                    }}
                  >
                    {`${r.userScore}/${r.totalScore}`}
                  </Box>
                </MenuItem>
              );
            })}
            {!currentOrg?.isPaidUser && (
              <>
                <Divider sx={{ my: 0.5 }} />
                <MenuItem
                  onClick={() => {
                    setHistoryAnchorEl(null);
                    setUpgradeDialogSource(
                      UpgradeDialogSource.template_evaluation_limit,
                    );
                  }}
                  sx={{ py: 1 }}
                >
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, color: "primary.main" }}
                  >
                    Upgrade for more evaluations
                  </Typography>
                </MenuItem>
              </>
            )}
          </Menu>

          {canRestoreFromRubric && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Viewing a past evaluation.{" "}
                <Link
                  component="button"
                  variant="caption"
                  underline="hover"
                  onClick={handleOpenRevisionPreview}
                  sx={{ cursor: "pointer" }}
                >
                  Show content at this revision
                </Link>
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 1,
              mb: 1,
            }}
          >
            <Typography
              variant="h6"
              textAlign={"center"}
              sx={
                isBusy
                  ? {
                      background:
                        "linear-gradient(90deg, #555 0%, #000 40%, #555 60%, #000 100%)",
                      backgroundSize: "200% auto",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                      animation: "shimmer 1.8s linear infinite",
                      "@keyframes shimmer": {
                        "0%": { backgroundPosition: "200% center" },
                        "100%": { backgroundPosition: "0% center" },
                      },
                    }
                  : {}
              }
            >
              {loading
                ? "We are evaluating your template... "
                : autoScoring
                  ? "We're scoring your new template... 🚀"
                  : rubric
                    ? "Here's Your Latest Template Score 🚀"
                    : "Evaluate your template"}
            </Typography>
          </Box>

          <Typography
            textAlign={"center"}
            variant="body2"
            sx={{ mb: rubric ? 1 : 3 }}
          >
            {isBusy ? (
              <>
                <Box
                  component="span"
                  sx={{
                    display: "inline-block",
                    animation: "bobUpDown 1.2s ease-in-out infinite",
                    "@keyframes bobUpDown": {
                      "0%, 100%": { transform: "translateY(0)" },
                      "50%": { transform: "translateY(-4px)" },
                    },
                  }}
                >
                  ⏳
                </Box>
                {loading
                  ? " This may take up to a minute..."
                  : " Hang tight, we're scoring the template you just created..."}
              </>
            ) : rubric ? (
              "Your evaluation is complete—review the full breakdown below."
            ) : (
              "Run a full evaluation to score this template and unlock every suggestion."
            )}
          </Typography>

          {rubric && !isBusy && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 3, display: "block" }}
            >
              Evaluated on{" "}
              {new Date(rubric.createdAt * 1000).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
            </Typography>
          )}
        </>
      )}

      <HalfDoughnut rubric={rubric} loading={isBusy} readyToGo={isReadyToGo} />

      {isReadyToGo ? (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              p: 2,
              mt: 2,
              borderRadius: 2,
              backgroundColor: "#F7F6F0",
            }}
          >
            <Box
              sx={{
                flexShrink: 0,
                width: 40,
                height: 40,
                borderRadius: "50%",
                backgroundColor: "#DBEFDD",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box
                component="span"
                className="fa-solid fa-paper-plane"
                sx={{ fontSize: 16, color: "#165C4E" }}
              />
            </Box>
            <Typography variant="body2">
              Your template has everything a brand needs to say yes — start
              pitching and let the results speak for themselves.
            </Typography>
          </Box>

          <Box
            sx={{ display: "flex", justifyContent: "center", gap: 3, mt: 2 }}
          >
            <Link
              component="button"
              variant="body2"
              underline="hover"
              onClick={() => setBreakdownOpen((prev) => !prev)}
              sx={{ cursor: "pointer" }}
            >
              {breakdownOpen ? "Hide breakdown ▲" : "View breakdown ▼"}
            </Link>
            <Link
              component="button"
              variant="body2"
              underline="hover"
              onClick={handleEvaluate}
              sx={{ cursor: "pointer", color: "text.secondary" }}
            >
              Re-evaluate
            </Link>
          </Box>

          {breakdownOpen && rubric && (
            <Rubrics rubric={rubric} showAll={isMobileScreen} />
          )}
        </>
      ) : (
        <>
          {!isBusy && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1.5,
                position: "relative",
                mt: 1,
              }}
            >
              <Button
                onClick={handleEvaluate}
                disabled={
                  !template || !(template?.body && template?.body?.length > 0)
                }
                sx={{
                  bgcolor: "#014638",
                  color: "#F7EE6F",
                  borderRadius: 999,
                  textTransform: "none",
                  fontSize: 15,
                  px: 3.25,

                  boxShadow: "0 8px 22px rgba(1, 70, 56, 0.2)",
                  transition:
                    "background 0.15s, transform 0.12s, box-shadow 0.15s",
                  "&:hover": {
                    bgcolor: "#165C4E",
                    transform: "translateY(-1px)",
                    boxShadow: "0 12px 26px rgba(1, 70, 56, 0.26)",
                  },
                  "&:active": { transform: "translateY(0)" },
                  "&.Mui-disabled": {
                    bgcolor: grey[300],
                    color: grey[500],
                    boxShadow: "none",
                  },
                }}
              >
                <Box
                  component="i"
                  className="fa-solid fa-bolt"
                  sx={{ fontSize: 12, mr: 0.5 }}
                />{" "}
                {rubric && rubric?.fields.length > 0
                  ? "Re-evaluate my template"
                  : "Evaluate my template"}
              </Button>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  fontSize: 11.5,
                  color: "text.secondary",
                }}
              >
                <Box
                  component="i"
                  className="fa-regular fa-clock"
                  sx={{ fontSize: 12 }}
                />
                Takes ~60 seconds
              </Box>
            </Box>
          )}

          {rubric && !isBusy && (
            <Rubrics rubric={rubric} showAll={isMobileScreen} />
          )}
        </>
      )}

      <Dialog
        open={revisionPreviewOpen}
        onClose={() => setRevisionPreviewOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Content from this evaluation
          {rubric && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", fontWeight: 400 }}
            >
              Evaluated on{" "}
              {new Date(rubric.createdAt * 1000).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {rubric?.content &&
            (() => {
              const { subject, body } = parseRubricContent(rubric.content);
              return (
                <>
                  {subject && (
                    <>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 600 }}
                      >
                        SUBJECT
                      </Typography>
                      <Box
                        sx={{ mt: 0.5, mb: 2 }}
                        dangerouslySetInnerHTML={{ __html: subject }}
                      />
                      <Divider sx={{ mb: 2 }} />
                    </>
                  )}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 600 }}
                  >
                    BODY
                  </Typography>
                  <Box
                    sx={{ mt: 0.5 }}
                    dangerouslySetInnerHTML={{ __html: body }}
                  />
                </>
              );
            })()}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRevisionPreviewOpen(false)}>Cancel</Button>
          <Button
            disableElevation
            variant="contained"
            onClick={handleConfirmRestore}
          >
            Restore to this version
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default TemplateRubrics;

// Rubric `content` is stored on the server as either `"Subject: <s>\n\nBody: <b>"`
// or just the raw body. Reverse that here.
const parseRubricContent = (
  content: string,
): { subject: string; body: string } => {
  const SUBJECT_PREFIX = "Subject: ";
  const BODY_SEPARATOR = "\n\nBody: ";
  if (!content.startsWith(SUBJECT_PREFIX)) {
    return { subject: "", body: content };
  }
  const sepIdx = content.indexOf(BODY_SEPARATOR);
  if (sepIdx === -1) {
    return { subject: "", body: content };
  }
  return {
    subject: content.slice(SUBJECT_PREFIX.length, sepIdx),
    body: content.slice(sepIdx + BODY_SEPARATOR.length),
  };
};
